import type { SiteViewer } from '@/lib/site-viewer/site-viewer'
import { SiteViewerControls, ControlEvent } from '@/lib/site-viewer/siteViewerControls'
import type { Duration } from '@/lib/site-viewer/tween-camera'
import { tweenCamera } from '@/lib/site-viewer/tween-camera'
import { debounce } from '@/lib/utils'
import { DEFAULT_WEB_ZOOM } from '@/lib/site-viewer/constants'
import type { Vec2Data } from '@/entities/model3d'

class HostSiteControls extends SiteViewerControls {
  private _killSetInitialTween: () => void = () => {}
  private _killPositionTween: () => void = () => {}
  private _isSettingInitialView: boolean = false
  private _isInteracting: boolean = false
  private _stopPublishing: boolean = true
  protected _onPointerDown: (event: PointerEvent) => void = () => {}
  protected _onPointerCancel: (event: PointerEvent) => void = () => {}

  override attach(viewer: SiteViewer) {
    super.attach(viewer)

    const controls = this.orbitControls
    if (!controls) return

    const debouncedStopTween = debounce(() => {
      this.isTweening = false
    }, 150)

    controls.addEventListener('change', () => {
      this.dispatchEvent({ type: ControlEvent.ViewChangedEvent })
      debouncedStopTween()
    })

    controls.addEventListener('start', e => {
      this.isTweening = true
      this._isInteracting = true
      this._stopPublishing = false
    })

    controls.addEventListener('end', e => {
      this._isInteracting = false
      debouncedStopTween()
    })

    this._domElements.forEach(el => {
      el.addEventListener('wheel', this._onDocumentMouseWheel)
      el.addEventListener('pointerdown', this._onPointerDown)
      el.addEventListener('pointercancel', this._onPointerCancel)
    })
  }

  update() {
    this.orbitControls?.update()
  }

  setInitialView({ position, tweenDuration }: { position?: Vec2Data; tweenDuration?: number }): Promise<void> {
    return new Promise(resolve => {
      if (!this.viewer) return resolve()

      this._isSettingInitialView = true

      this._killSetInitialTween()
      this._killSetInitialTween = tweenCamera(
        this.viewer.camera,
        { target: position },
        position && this._isEquivalentPosition(position) ? 0 : tweenDuration,
        () => {
          this.isTweening = true
          if (this.orbitControls) this.orbitControls.enabled = false
          this.orbitControls?.update()
        },
        () => {
          this.isTweening = false
          if (this.orbitControls) this.orbitControls.enabled = true
          this._isSettingInitialView = false
          resolve()
        }
      )
    })
  }

  /**
   * Reposition the camera such that we are looking in the direction of the target position by getting the vector
   * between our initial position and the target position and adding it to our target position
   * @param position is the UV coordinate that we would like the camera to be looking in the direction of
   * @param duration is the time in which we spend tweening the initial position to the new position
   */
  lookAt({ position, tweenDuration, onComplete }: { position: Vec2Data; tweenDuration?: Duration; onComplete?: () => void }) {
    if (!this.viewer || !position || !this.orbitControls) return

    this._killPositionTween()
    this._killPositionTween = tweenCamera(
      this.viewer.camera,
      { target: position },
      this._isEquivalentPosition(position) ? 0.2 : tweenDuration,
      newPos => {
        this.isTweening = true
        if (this.orbitControls) this.orbitControls.enabled = false
        // ControlEvent.ViewChangedEvent will be emitted by the controls listener
        this.orbitControls?.update()
      },
      () => {
        this.isTweening = false
        onComplete?.()
        if (this.orbitControls) this.orbitControls.enabled = true
      }
    )
  }

  override detach() {
    super.detach()
    this.orbitControls?.dispose()
    this._domElements.forEach(el => {
      el.removeEventListener('wheel', this._onDocumentMouseWheel)
      el.removeEventListener('pointerdown', this._onPointerDown)
      el.removeEventListener('pointercancel', this._onPointerCancel)
    })
    this.viewer = null
  }

  startAutoRotate() {
    if (!this.orbitControls) return

    this.orbitControls.autoRotate = true
    this.orbitControls.enabled = false
  }

  resetZoom() {
    this.updateZoom({ fov: DEFAULT_WEB_ZOOM, shouldPublish: true, tweenDuration: 0 })
  }

  protected override _publishPan() {
    if (this._stopPublishing || this._isSettingInitialView) return

    if (!this._isInteracting) this._setStopPublishing()

    super._publishPan()
  }

  protected get _domElements() {
    return [this.viewer?.cssRenderer.domElement, this.viewer?.renderer.domElement].filter((x): x is HTMLElement => !!x)
  }

  private _setStopPublishing = debounce(() => {
    if (!this._isInteracting) this._stopPublishing = true
  }, 150)

  private _onDocumentMouseWheel = (event: WheelEvent): void => {
    if (!this.viewer) return
    event.preventDefault()
    event.stopPropagation()

    this.viewer.setFov(this.viewer.camera.fov + event.deltaY * 0.05)

    this.dispatchEvent({ type: ControlEvent.ViewChangedEvent })
    this.dispatchEvent({ type: ControlEvent.Wheel })
    this._publishZoom()
  }
}

export { HostSiteControls }
